import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';
import { CatalogoOpcaoResponse, PrestadorListItem, RelatorioSelecaoPjExportResponse, RelatorioSelecaoPjJobCreatedResponse, RelatorioSelecaoPjLoteExportRequest, RelatorioSelecaoPjPesquisaFiltros } from './relatorio-selecao-pj.model';
import { DateUtils } from '@/shared/utils/date-utils';

/**
 * Acesso HTTP ao endpoint de relatório por seleção de prestadores.
 * Expõe pesquisa de estabelecimentos (Abas 1–9, seguindo as regras do legado SIEM-CNP) e o fluxo de
 * exportação PDF em lote (síncrono com fallback assíncrono — HTTP 200 com PDF direto ou 202 com jobId
 * para polling via `RelatorioSelecaoPjJobService`), mesmo padrão do Relatório por Seleção de Médicos.
 */
@Injectable({ providedIn: 'root' })
export class RelatorioSelecaoPjService extends BaseService {
    readonly PATH = SICOM_PATHS.registro.prestador.reports.relatorioSelecaoPj;

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    /** Pesquisa prestadores pelos filtros das Abas 1–9 (máx. 200 registros, sem paginação). */
    pesquisar(filtros: Partial<RelatorioSelecaoPjPesquisaFiltros>): Observable<PrestadorListItem[]> {
        return this.http.get<PrestadorListItem[]>(`${this.PATH}/pesquisar`, { params: this.toHttpParams(filtros) });
    }

    /** Catálogo de Classificação (Aba 9 — Outros Filtros). */
    listarClassificacoes(): Observable<CatalogoOpcaoResponse[]> {
        return this.http.get<CatalogoOpcaoResponse[]>(`${this.PATH}/catalogo-classificacao`);
    }

    /** Gera o PDF combinando todos os prestadores do lote — 200 (PDF inline) ou 202 (jobId para polling). */
    exportarPdfLote(request: RelatorioSelecaoPjLoteExportRequest): Observable<HttpResponse<RelatorioSelecaoPjExportResponse | RelatorioSelecaoPjJobCreatedResponse>> {
        return this.http.post<RelatorioSelecaoPjExportResponse | RelatorioSelecaoPjJobCreatedResponse>(`${this.PATH}/exportar-pdf-lote`, request, { observe: 'response' });
    }

    private toHttpParams(f: Partial<RelatorioSelecaoPjPesquisaFiltros>): HttpParams {
        let params = new HttpParams();
        const setIfPresent = (key: string, value: string | number | null | undefined): void => {
            if (value !== null && value !== undefined && value !== '') {
                params = params.set(key, String(value));
            }
        };
        const appendList = (key: string, values: number[] | undefined): void => {
            (values ?? []).forEach((v) => {
                params = params.append(key, String(v));
            });
        };

        setIfPresent('nuCrm', f.nuCrm);
        setIfPresent('sgUf', f.sgUf);
        setIfPresent('nuCgcPj', f.nuCgcPj);
        setIfPresent('regCad', f.regCad);
        setIfPresent('idSituacaoRegistro', f.idSituacaoRegistro);
        setIfPresent('idTipoInscricao', f.idTipoInscricao);
        setIfPresent('idSituacao', f.idSituacao);
        setIfPresent('dtSituacaoInicio', f.dtSituacaoInicio ? DateUtils.toIsoDate(f.dtSituacaoInicio) : undefined);
        setIfPresent('dtSituacaoFim', f.dtSituacaoFim ? DateUtils.toIsoDate(f.dtSituacaoFim) : undefined);
        setIfPresent('nmPrestador', f.nmPrestador);
        setIfPresent('nmFantasia', f.nmFantasia);
        setIfPresent('idEspecialidade', f.idEspecialidade);
        setIfPresent('idAreaAtuacao', f.idAreaAtuacao);

        appendList('idsRecursoHumano', f.idsRecursoHumano);
        setIfPresent('contemTodosRh', f.contemTodosRh ? 'true' : undefined);
        appendList('idsRecursoMaterial', f.idsRecursoMaterial);
        setIfPresent('contemTodosRecursoMaterial', f.contemTodosRecursoMaterial ? 'true' : undefined);
        appendList('idsServico', f.idsServico);
        setIfPresent('contemTodosServico', f.contemTodosServico ? 'true' : undefined);
        appendList('idsAtividade', f.idsAtividade);
        setIfPresent('contemTodosAtividade', f.contemTodosAtividade ? 'true' : undefined);
        setIfPresent('emitirApenasComCertificadoVencido', f.emitirApenasComCertificadoVencido ? 'true' : undefined);

        setIfPresent('idSituacaoHistorico', f.idSituacaoHistorico);
        setIfPresent('dtHistoricoInicio', f.dtHistoricoInicio ? DateUtils.toIsoDate(f.dtHistoricoInicio) : undefined);
        setIfPresent('dtHistoricoFim', f.dtHistoricoFim ? DateUtils.toIsoDate(f.dtHistoricoFim) : undefined);
        setIfPresent('tipoEstabelecimento', f.tipoEstabelecimento);
        setIfPresent('idClassificacaoOpcao', f.idClassificacaoOpcao);
        setIfPresent('tipoGestao', f.tipoGestao);
        setIfPresent('dtInscricaoInicio', f.dtInscricaoInicio ? DateUtils.toIsoDate(f.dtInscricaoInicio) : undefined);
        setIfPresent('dtInscricaoFim', f.dtInscricaoFim ? DateUtils.toIsoDate(f.dtInscricaoFim) : undefined);
        setIfPresent('idMunicipio', f.idMunicipio);
        setIfPresent('idOrgaoDelegacia', f.idOrgaoDelegacia);
        setIfPresent('sgUfDelegacia', f.sgUfDelegacia);

        return params;
    }
}
